/**
 * Comment threads drawn into the rendered document: a highlight over each
 * thread's range. The host maps source ranges to document positions and hands
 * them over with {@link setCommentMarks}; clicking a highlight opens its thread.
 */
import { Extension } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, type EditorState } from "@tiptap/pm/state";
import { Decoration, DecorationSet, type EditorView } from "@tiptap/pm/view";

export interface CommentMark {
  id: string;
  from: number;
  to: number;
  active: boolean;
}

const key = new PluginKey<DecorationSet>("commentHighlights");

function decorations(doc: PMNode, marks: CommentMark[]): DecorationSet {
  return DecorationSet.create(
    doc,
    marks.map((mark) =>
      Decoration.inline(
        mark.from,
        mark.to,
        {
          class: mark.active
            ? "cursor-pointer rounded-sm bg-amber-300/70 dark:bg-amber-500/45"
            : "cursor-pointer rounded-sm bg-amber-200/60 dark:bg-amber-500/25",
        },
        { commentId: mark.id },
      ),
    ),
  );
}

export function setCommentMarks(view: EditorView, marks: CommentMark[]): void {
  view.dispatch(
    view.state.tr.setMeta(key, marks).setMeta("addToHistory", false),
  );
}

/** Where a thread's highlight is now, after any edits since it was drawn. */
export function commentMarkRange(
  state: EditorState,
  id: string,
): { from: number; to: number } | null {
  const found = key
    .getState(state)
    ?.find(undefined, undefined, (spec) => spec.commentId === id)[0];
  return found ? { from: found.from, to: found.to } : null;
}

export const CommentHighlights = Extension.create<{
  onActivate: (id: string) => void;
}>({
  name: "commentHighlights",
  addOptions() {
    return { onActivate: () => {} };
  },
  addProseMirrorPlugins() {
    const { onActivate } = this.options;
    return [
      new Plugin({
        key,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, set) {
            const marks: CommentMark[] | undefined = tr.getMeta(key);
            return marks
              ? decorations(tr.doc, marks)
              : set.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations: (state) => key.getState(state),
          handleClick(view, pos) {
            const [hit] =
              key
                .getState(view.state)
                ?.find(
                  pos,
                  pos,
                  (spec) => typeof spec.commentId === "string",
                ) ?? [];
            if (!hit) return false;
            const id: string = hit.spec.commentId;
            onActivate(id);
            return false;
          },
        },
      }),
    ];
  },
});
